import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from './lib/supabase';
import { User } from '@supabase/supabase-js';
import LoginForm from './components/LoginForm';
import ChatAssist from './components/ChatAssist';
import HomePage from './components/HomePage';
import Dashboard from './components/Dashboard';
import ContactList from './components/ContactList';
import ContactModal from './components/ContactModal';
import ContactViewModal from './components/ContactViewModal';
import ContactDetailPage from './components/contact/ContactDetailPage';
import CreateContactPage from './components/CreateContactPage';
import ContactCollections from './components/ContactCollections';
import CollectionDetailsPage from './components/CollectionDetailsPage';
import GHLManagement from './components/GHLManagement';
import Settings from './components/Settings';
import ArchivedContacts from './components/ArchivedContacts';
import ImportsManagement from './components/ImportsManagement';
import CollectionEditorPage from './components/CollectionEditorPage';
import PasswordResetPage from './components/PasswordResetPage';
import NoteMigrationDashboard from './components/NoteMigrationDashboard';
import GoogleContactMap from './components/GoogleContactMap';
import SquareLoader from './components/SquareLoader';
import Sidebar from './components/Sidebar';
import { Contact } from './types/Contact';
import { Shield, Users, TrendingUp, Phone, Menu, X } from 'lucide-react';
import { GroupProvider, useGroupContext } from './lib/groups/context';
// Using core for group operations and profiles
import { getPreferredVaultIdCore, getGroupByIdCore, createVaultCore, getStartViewCore, getUserProfileCore } from '@gv/core';
import VaultGate from './components/VaultGate';
import InviteAcceptPage from './components/InviteAcceptPage';
import CreateGroupPage from './components/CreateGroupPage';
import { useNotifications } from './hooks/useNotifications';
import NotificationToast from './components/NotificationToast';
import TodoPanel from './components/TodoPanel';
import NewCalendarView from './components/NewCalendarView';
import ShareRouter from './publicPages/ShareRouter';
import ContactExportHelp from './components/ContactExportHelp';
import QuickAddContact from './components/QuickAddContact';
import { backfillTodosForUser } from './lib/backfill';
import { listContactsCore, listContactsForGroupCore, getContactCore } from './lib/contactsCore';
import { getEventOwnerContactId } from './lib/events';

function AppInner() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingInviteToken, setPendingInviteToken] = useState<string | null>(null);
  // Initialize view from cached Startup Page to avoid initial dashboard flash
  const initialStartView = useMemo(() => {
    try {
      const raw = localStorage.getItem('gv:startView');
      const allowed = new Set([
        'dashboard', 'contacts', 'collections', 'map', 'todo', 'calendar', 'create-contact', 'quick-add-contact', 'contacts-search', 'settings', 'assistant'
      ]);
      if (!raw || !allowed.has(raw)) return 'dashboard';
      return raw === 'contacts-search' ? 'contacts' : raw;
    } catch { return 'dashboard'; }
  }, []);
  const [currentView, setCurrentView] = useState(initialStartView);
  // Whether we have applied the user's startup preference this session
  const [startupApplied, setStartupApplied] = useState<boolean>(() => {
    try { return !!localStorage.getItem('gv:startView'); } catch { return false; }
  });
  const [navigationStack, setNavigationStack] = useState<string[]>([initialStartView]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [viewContactCollectionContext, setViewContactCollectionContext] = useState<string | undefined>(undefined);
  const [viewContactCollectionName, setViewContactCollectionName] = useState<string | undefined>(undefined);
  const [viewContactCollectionId, setViewContactCollectionId] = useState<string | undefined>(undefined);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [personalProfile, setPersonalProfile] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [shouldFocusSearch, setShouldFocusSearch] = useState(() => {
    try { return localStorage.getItem('gv:startView') === 'contacts-search'; } catch { return false; }
  });
  // Auth: email code is the only requirement (MFA removed)
  const [contactsTierFilter, setContactsTierFilter] = useState('all');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [editingCollectionId, setEditingCollectionId] = useState<string | null>(null);
  const [collectionSelectionMode, setCollectionSelectionMode] = useState<{
    enabled: boolean;
    collectionId: string;
    collectionName: string;
    existingMemberIds: string[];
  }>({ enabled: false, collectionId: '', collectionName: '', existingMemberIds: [] });
  // Prefill query when navigating into People search from other screens
  const [pendingContactsSearchTerm, setPendingContactsSearchTerm] = useState<string>('');
  // People tabs removed; single directory view
  // Legacy inline create-group UI removed in favor of full page
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const { notification, showNotification, hideNotification } = useNotifications();

  // Group context (make available before any effects that depend on it)
  const { activeGroupId, groups, accessState, vaultSlug, reload } = useGroupContext();
  // Clear contacts immediately on vault switch to avoid stale flash
  const lastGroupIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastGroupIdRef.current === null) {
      lastGroupIdRef.current = activeGroupId || null;
      return;
    }
    if (lastGroupIdRef.current !== (activeGroupId || null)) {
      // Vault changed: clear paginated data immediately
      setContacts([]);
      setHasMoreContacts(true);
      // Also clear selection context
      setViewingContact(null);
      setSelectedCollectionId(null);
      lastGroupIdRef.current = activeGroupId || null;
    }
  }, [activeGroupId]);

  // Ref to prevent infinite loop issues
  const fetchContactsRef = useRef<typeof fetchContacts | null>(null);
  const loadingMoreRef = useRef(false);
  // Refs to keep latest state inside single popstate handler
  const isModalOpenRef = useRef(isModalOpen);
  const isViewModalOpenRef = useRef(isViewModalOpen);
  const navigationStackRef = useRef<string[]>(navigationStack);
  const currentViewRef = useRef(currentView);
  const collectionSelectionModeRef = useRef(collectionSelectionMode);
  const isHandlingPopstateRef = useRef(false);
  // Maintain a stack of previously viewed contacts when navigating between contacts inside contact-detail
  const contactHistoryRef = useRef<string[]>([]);
  useEffect(() => { isModalOpenRef.current = isModalOpen; }, [isModalOpen]);
  useEffect(() => { isViewModalOpenRef.current = isViewModalOpen; }, [isViewModalOpen]);
  useEffect(() => { navigationStackRef.current = navigationStack; }, [navigationStack]);
  useEffect(() => { currentViewRef.current = currentView; }, [currentView]);
  useEffect(() => { collectionSelectionModeRef.current = collectionSelectionMode; }, [collectionSelectionMode]);


  // Authentication setup and management
  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Check if this is a password recovery redirect (Supabase uses hash fragments)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const type = hashParams.get('type');
      const currentPath = window.location.pathname;

      // If this is specifically the password reset route, handle it separately
      if (currentPath === '/auth/reset-password' || type === 'recovery') {
        // Let PasswordResetPage handle the auth
        setUser(null);
      } else if (type === 'magiclink' || type === 'signup') {
        setUser(session?.user ?? null);
      } else {
        setUser(session?.user ?? null);
      }
      // Capture invite token in URL and persist for post-login accept flow
      try {
        const url = new URL(window.location.href);
        const token = url.searchParams.get('invite');
        if (token) {
          localStorage.setItem('gv:pendingInviteToken', token);
          setPendingInviteToken(token);
        } else {
          const cached = localStorage.getItem('gv:pendingInviteToken');
          if (cached) setPendingInviteToken(cached);
        }
      } catch { /* ignore */ }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {

      // Handle different auth events
      if (event === 'PASSWORD_RECOVERY') {
        window.history.replaceState({}, document.title, window.location.pathname);
        setUser(session?.user ?? null);
      } else if (event === 'SIGNED_IN') {
        // Check if this was from a recovery link
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');

        if (type === 'recovery') {
          // Still in recovery mode, don't auto-login yet
          setUser(null);
        } else {
          // Normal sign in or magic link
          setUser(session?.user ?? null);
          try {
            if (type === 'magiclink' || type === 'signup') {
              localStorage.setItem('gv:needsPasswordSetup', '1');
            }
          } catch { }
          // If signed in at root and user intended to create a vault, trigger create-group view
          try {
            const flag = localStorage.getItem('gv:pendingCreateVault');
            if (flag && (!window.location.pathname.split('/').filter(Boolean)[0])) {
              localStorage.removeItem('gv:pendingCreateVault');
              setShowCreateGroup(true);
              handleViewChange('create-group');
            }
          } catch { }
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      } else {
        // For other events, check if we're still in recovery mode
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get('type');

        if (type === 'recovery') {
          // Keep showing LoginForm for password reset
          console.log('Recovery mode maintained');
          setUser(null);
        } else {
          setUser(session?.user ?? null);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // MFA fully removed

  // Apply Startup Page preference once after entering a vault session
  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      if (!vaultSlug) return; // only apply once inside a vault
      if (startupApplied) return;
      if (accessState !== 'ok') return;

      // Fast path: use cached preference for instant navigation; sync DB later
      try {
        const raw = localStorage.getItem('gv:startView');
        const allowed = new Set(['dashboard', 'contacts', 'collections', 'map', 'todo', 'calendar', 'create-contact', 'quick-add-contact', 'contacts-search', 'settings', 'assistant']);
        if (raw && allowed.has(raw)) {
          if (raw === 'contacts-search') {
            setShouldFocusSearch(true);
            handleViewChange('contacts');
          } else {
            handleViewChange(raw);
          }
          setStartupApplied(true);
          return;
        }
      } catch { /* ignore */ }

      // Fallback to DB if no cache
      try {
        const sv = (await getStartViewCore(supabase as any, user.id)) || 'dashboard';
        try { localStorage.setItem('gv:startView', sv); } catch { }
        if (sv === 'contacts-search') {
          setShouldFocusSearch(true);
          handleViewChange('contacts');
        } else {
          handleViewChange(sv);
        }
        setStartupApplied(true);
      } catch {
        // keep not applied to retry on next tick if needed
      }
    })();
  }, [user?.id, vaultSlug, accessState, startupApplied]);

  // Run a gentle backfill once per session once the user is known
  useEffect(() => {
    (async () => {
      try {
        if (!user?.id) return;
        await backfillTodosForUser(user.id);
      } catch {
        // non-fatal
      }
    })();
  }, [user?.id]);

  // Safety effect: Auto-navigate back if we're in contact-detail view but have no contact
  useEffect(() => {
    if (currentView === 'contact-detail' && !viewingContact) {
      // This indicates a state inconsistency, navigate back safely
      console.log('Contact detail view without contact, navigating back...');
      handleBackFromContactDetail();
    }
  }, [currentView, viewingContact]);

  // Always reset scroll to top on view changes and modal navigations,
  // but skip if we're restoring a saved position for collections, contacts, or returning to a contact detail
  useEffect(() => {
    const scrollToTop = () => {
      const container = document.getElementById('main-scroll-container');
      if (container && typeof (container as any).scrollTo === 'function') {
        try { (container as any).scrollTo({ top: 0, behavior: 'instant' as any }); } catch { container.scrollTop = 0; }
      } else {
        try { window.scrollTo({ top: 0, behavior: 'instant' as any }); } catch { window.scrollTo(0, 0); }
      }
    };

    try {
      // If returning to a collection view with a saved scroll position, skip the auto scroll-to-top
      if (currentView === 'collection-view' && selectedCollectionId) {
        const key = `gv:scroll:collection:${selectedCollectionId}`;
        const saved = sessionStorage.getItem(key);
        if (saved) return; // We'll restore after the collection loads
      }
      // If returning to contacts with a saved scroll position, skip the auto scroll-to-top
      if (currentView === 'contacts') {
        const savedContacts = sessionStorage.getItem('gv:scroll:contacts');
        if (savedContacts) return; // Restored by ContactList after render
      }
      // If returning to a prior contact-detail state, skip auto scroll so we can restore precise position
      if (currentView === 'contact-detail') {
        const pending = sessionStorage.getItem('gv:return:pending');
        if (pending === '1') return;
      }
    } catch { /* no-op */ }

    // Default behavior: new pages start at the top
    scrollToTop();
  }, [currentView, isModalOpen, isViewModalOpen, selectedContact, viewingContact, selectedCollectionId]);

  // Handle browser back button to navigate between views
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      // Suppress pushState while handling back navigation
      isHandlingPopstateRef.current = true;
      const finish = () => { isHandlingPopstateRef.current = false; };
      try {
        // If we're in contact-detail and have navigated between contacts, pop back to the previous contact
        if (currentViewRef.current === 'contact-detail' && contactHistoryRef.current.length > 0) {
          const prevId = contactHistoryRef.current.pop()!;
          (async () => {
            try {
              const row = await getContactCore(supabase as any, prevId);
              if (row) {
                setViewingContact(row as any as Contact);
                // Mirror state without changing view to keep stack consistent
                try { window.history.replaceState({ view: 'contact-detail', contactId: prevId }, '', window.location.href); } catch { }
              }
            } catch (err) {
              console.error('Failed to navigate back to previous contact', err);
            } finally {
              finish();
            }
          })();
          return; // Early return; finish will be called in async block
        }
        // Close any open modals first
        if (isModalOpenRef.current || isViewModalOpenRef.current) {
          closeContactModal();
          closeContactViewModal();
          return finish();
        }

        // Special cases that require custom cleanup/state management
        if (currentViewRef.current === 'contact-detail') {
          handleBackFromContactDetail();
          return finish();
        }

        if (currentViewRef.current === 'contacts' && collectionSelectionModeRef.current.enabled) {
          handleNavigateBackFromCollectionSelection();
          return finish();
        }

        if (currentViewRef.current === 'collection-view') {
          handleBackFromCollection();
          return finish();
        }

        // Generic stack-based back navigation, but treat Quick Add and Full Form as one page
        const stack = navigationStackRef.current;
        if (stack.length > 1) {
          const newStack = [...stack];
          newStack.pop();
          // Skip any trailing add views so we go back to the last non-add view
          while (newStack.length > 0 && isAddView(newStack[newStack.length - 1])) newStack.pop();
          const previousView = newStack[newStack.length - 1] || 'dashboard';

          // Clear collection selection mode when leaving contacts
          if (currentViewRef.current === 'contacts' && previousView !== 'contacts') {
            setCollectionSelectionMode({ enabled: false, collectionId: '', collectionName: '', existingMemberIds: [] });
          }

          setNavigationStack(newStack.length > 0 ? newStack : ['dashboard']);
          setCurrentView(previousView);
        } else {
          // No internal history to handle; allow browser to navigate naturally
        }
      } finally {
        finish();
      }
    };

    // Seed current history entry with state; don't push an extra entry
    try {
      window.history.replaceState({ view: currentViewRef.current }, '', window.location.href);
    } catch { }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Auto-close sidebar on mobile and manage responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
        // Always un-collapse on mobile so a previously "cute" collapsed desktop sidebar
        // does NOT remain tiny on small screens.
        if (sidebarCollapsed) setSidebarCollapsed(false);
      }
      // Expose CSS variable for current sidebar width used by any fixed footers/toolbars.
      const root = document.documentElement;
      const width = window.innerWidth >= 1024 ? (sidebarCollapsed ? 64 : 256) : 0; // tailwind w-16=64px, w-64=256px
      root.style.setProperty('--gv-sidebar-width', width + 'px');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update CSS var when collapse toggles on desktop without resize
  useEffect(() => {
    if (window.innerWidth >= 1024) {
      const width = sidebarCollapsed ? 64 : 256;
      document.documentElement.style.setProperty('--gv-sidebar-width', width + 'px');
    }
  }, [sidebarCollapsed]);

  // Navigate to People after group creation
  useEffect(() => {
    const handler = () => {
      setSelectedCollectionId(null);
      setViewingContact(null);
      setEditingContact(null);
      setContacts([]);
      handleViewChange('contacts');
      setShowCreateGroup(false);
    };
    window.addEventListener('gv:navigateToPeople' as any, handler as any);
    return () => window.removeEventListener('gv:navigateToPeople' as any, handler as any);
  }, []);

  // Navigate to Create Group full page
  useEffect(() => {
    const handler = () => {
      // New behavior: go to full-page group creation view
      setShowCreateGroup(false);
      handleViewChange('create-group');
    };
    window.addEventListener('gv:navigateToCreateGroup' as any, handler as any);
    return () => window.removeEventListener('gv:navigateToCreateGroup' as any, handler as any);
  }, []);

  // Allow internal components to navigate directly to settings
  useEffect(() => {
    const handler = () => {
      handleViewChange('settings');
    };
    window.addEventListener('gv:navigateToSettings' as any, handler as any);
    return () => window.removeEventListener('gv:navigateToSettings' as any, handler as any);
  }, []);

  // Global simple navigate event (used by calendar to jump to To‑Do on mobile)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { view?: string } | undefined;
      const view = detail?.view;
      if (!view) return;
      handleViewChange(view);
    };
    window.addEventListener('gv:navigate' as any, handler as any);
    return () => window.removeEventListener('gv:navigate' as any, handler as any);
  }, []);

  // Removed redundant explicit events to navigate between Quick Add and Full Form.
  // Use the generic 'gv:navigate' event everywhere to avoid double-dispatch.

  // Global event: search People with a prefilled query
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { query?: string } | undefined;
      const query = (detail?.query || '').trim();
      try { sessionStorage.setItem('gv:lastAssistantSearch', query); } catch { }
      setPendingContactsSearchTerm(query);
      setShouldFocusSearch(true);
      handleViewChange('contacts');
    };
    window.addEventListener('gv:searchPeople' as any, handler as any);
    return () => window.removeEventListener('gv:searchPeople' as any, handler as any);
  }, []);

  // Global navigation from relationship modal avatar clicks
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { contactId?: string } | undefined;
      const id = detail?.contactId;
      if (!id) return;

      // Try to find the contact locally first
      const target = contacts.find(c => c.id === id);
      if (target) {
        openContactViewModal(target);
        return;
      }

      // Fetch from core if not in local list (no silent fallbacks)
      (async () => {
        try {
          const row = await getContactCore(supabase as any, id);
          if (row) openContactViewModal(row as any as Contact);
        } catch (err) {
          console.error('Failed to open contact by id:', err);
        }
      })();
    };
    window.addEventListener('gv:openContact' as any, handler as any);
    return () => window.removeEventListener('gv:openContact' as any, handler as any);
  }, [contacts]);

  // Global navigation to open an event by id (from To-Do panel)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { eventId?: string; contactId?: string | null } | undefined;
      const eventId = detail?.eventId;
      const contactId = detail?.contactId || undefined;
      if (!eventId) return;

      const openForContact = async (cid?: string) => {
        if (!cid) return;
        try {
          const row = await getContactCore(supabase as any, cid);
          if (row) {
            openContactViewModal(row as any as Contact);
            // After navigation, ask the detail page to open the event
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('gv:showEvent', { detail: { eventId } } as any));
            }, 350);
          }
        } catch (err) {
          console.error('Failed to open event via contact', err);
        }
      };

      if (contactId) {
        openForContact(contactId);
      } else {
        // Look up the event to determine its contact via core helper
        (async () => {
          try {
            const cid = await getEventOwnerContactId(eventId);
            if (cid) openForContact(cid);
          } catch (err) {
            console.error('Failed to resolve contact for event', err);
          }
        })();
      }
    };
    window.addEventListener('gv:openEvent' as any, handler as any);
    return () => window.removeEventListener('gv:openEvent' as any, handler as any);
  }, []);

  // Removed duplicate auth initialization effect to prevent UI flicker

  // Derive a display name for the current vault from its slug
  const vaultName = useMemo(() => {
    if (!vaultSlug) return '';
    try {
      const pretty = String(vaultSlug)
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return pretty;
    } catch { return String(vaultSlug); }
  }, [vaultSlug]);

  // Cached start view detection no longer needed for gating the hold; we hold until applied

  // Resolve dynamic vault logo from active group context
  const vaultLogoUrl = useMemo(() => {
    const defaultLogo = "https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp";
    const activeGroup = groups.find(g => g.group.id === activeGroupId);
    let url = (activeGroup?.group as any)?.image_url || defaultLogo;
    try {
      const b = activeGroupId ? localStorage.getItem(`gv:logoBust:${activeGroupId}`) : null;
      if (b) url = `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(b)}`;
    } catch { }
    return url;
  }, [groups, activeGroupId]);

  const fetchContacts = useCallback(async (page = 0, append = false, retryCount = 0) => {
    if (contactsLoading) {
      return;
    }
    if (!user) return; // Don't fetch if user is not authenticated

    setContactsLoading(true);
    try {
      const batchSize = 50; // Much smaller initial load
      const from = page * batchSize;

      const opts = { includeArchived: false as const, limit: batchSize, offset: from };
      const rows = activeGroupId
        ? await listContactsForGroupCore(supabase as any, activeGroupId, opts)
        : await listContactsCore(supabase as any, opts);

      const normalized = (rows as any[]) || [];
      if (append) {
        setContacts(prev => [...prev, ...normalized]);
      } else {
        setContacts(normalized);
      }
      const moreContactsAvailable = normalized.length === batchSize;
      setHasMoreContacts(moreContactsAvailable);
    } catch (error) {
      console.error('Error fetching contacts:', error);

      // Retry on network errors up to 3 times
      if (retryCount < 3 && (error.message?.includes('Failed to fetch') || error.message?.includes('Network'))) {
        setTimeout(() => {
          fetchContacts(page, append, retryCount + 1);
        }, 1000 * (retryCount + 1)); // Exponential backoff: 1s, 2s, 3s
        return;
      }
      // If group context isn't ready yet on a hard refresh, try once after a short delay
      if (!activeGroupId && vaultSlug && retryCount < 1) {
        setTimeout(() => fetchContacts(page, append, retryCount + 1), 500);
        return;
      }
    } finally {
      setContactsLoading(false);
    }
  }, [user, activeGroupId]); // Removed contactsLoading from dependencies to prevent recreation loop

  // Defer loading contacts until needed; still load lightweight personal profile
  useEffect(() => {
    if (user) {
      loadPersonalProfile();
    }
  }, [user]);

  // Load contacts when entering contacts-related views or when dashboard needs recent contacts
  useEffect(() => {
    if (!user) return;
    const needsContacts = currentView === 'contacts' || currentView === 'dashboard' || currentView === 'contact-detail';
    if (!needsContacts) return;
    fetchContacts(0, false);
  }, [user, currentView, activeGroupId]);

  // Listen for contactCreated events to inject new contact immediately (optimistic) then refresh first page
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { id?: string } | undefined;
      const id = detail?.id;
      if (!id) return;
      try {
        // Fetch the newly created contact row directly (no silent fallback)
        const row = await getContactCore(supabase as any, id);
        if (row) {
          setContacts(prev => {
            if (prev.find(c => c.id === row.id)) return prev; // already present
            // Prepend so it appears immediately in search/filter
            return [row as any as Contact, ...prev];
          });
        }
      } catch (err) {
        console.error('Failed to fetch newly created contact', err);
      } finally {
        // Refresh first page to align ordering/pagination; defer slightly to batch if rapid creates
        setTimeout(() => { fetchContacts(0, false); }, 350);
      }
    };
    window.addEventListener('gv:contactCreated' as any, handler as any);
    return () => window.removeEventListener('gv:contactCreated' as any, handler as any);
  }, [fetchContacts]);

  // When group context resolves after a hard refresh, refresh contacts only if a contacts-relevant view is active
  useEffect(() => {
    if (!user) return;
    if (accessState !== 'ok') return;
    const needsContacts = currentView === 'contacts' || currentView === 'dashboard' || currentView === 'contact-detail';
    if (!needsContacts) return;
    fetchContacts(0, false);
  }, [user, accessState, activeGroupId, currentView]);

  // Live updates: refresh contacts when any contact row for this user changes
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`contacts-realtime-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'contacts', filter: `user_id=eq.${user.id}` },
        (_payload) => {
          // Always refresh the first page so Recent Activity stays current
          fetchContacts(0, false);
        }
      )
      .subscribe();

    return () => {
      try { supabase.removeChannel(channel); } catch { /* ignore */ }
    };
  }, [user?.id, fetchContacts]);

  const loadPersonalProfile = async (retryCount = 0) => {
    if (!user) return;
    try {
      const prof = await getUserProfileCore(supabase as any, user.id);
      // personal_profile removed
    } catch (error: any) {
      console.error('Error loading personal profile (core):', error);
      // Retry on transient network errors up to 3 times
      if (retryCount < 3 && (error?.message?.includes('Failed to fetch') || error?.message?.includes('Network'))) {
        setTimeout(() => loadPersonalProfile(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      // Surface empty state if irrecoverable
      setPersonalProfile('');
    }
  };

  const resetToBasicView = useCallback(() => {
    setHasMoreContacts(true);
    setContacts([]);
    // This will trigger a fresh load of contacts in basic pagination mode
    fetchContacts();
  }, [fetchContacts]);

  const fetchAllContacts = useCallback(async () => {
    if (contactsLoading) return;
    if (!user) return; // Don't fetch if user is not authenticated

    setContactsLoading(true);
    try {
      // Load all contacts (unarchived) for filtering. Use a generous limit.
      const limit = 5000; // reasonably high for UI filtering; adjust if needed
      const rows = activeGroupId
        ? await listContactsForGroupCore(supabase as any, activeGroupId, { includeArchived: false as const, limit, offset: 0 })
        : await listContactsCore(supabase as any, { includeArchived: false as const, limit, offset: 0 });

      setContacts((rows as any[]) || []);
      setHasMoreContacts(false); // No more to load since we loaded all
    } catch (error) {
      console.error('Error fetching all contacts:', error);
    } finally {
      setContactsLoading(false);
    }
  }, [contactsLoading, user, activeGroupId]);

  // Clear pending invite token once accepted/declined by child screen
  const handleInviteDone = () => {
    try { localStorage.removeItem('gv:pendingInviteToken'); } catch { }
    setPendingInviteToken(null);
  };

  const loadMoreContacts = useCallback(async () => {
    if (!hasMoreContacts || contactsLoading || loadingMoreRef.current) {
      return;
    }

    loadingMoreRef.current = true;
    try {
      const currentPage = Math.floor(contacts.length / 50);
      await fetchContacts(currentPage + 1, true);
    } finally {
      loadingMoreRef.current = false;
    }
  }, [hasMoreContacts, contactsLoading, contacts.length, fetchContacts]);

  // Add scroll event listener for infinite scroll - AFTER loadMoreContacts is defined
  useEffect(() => {
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Only trigger for contacts view
      if (currentView !== 'contacts') return;

      // Don't trigger if already loading or no more contacts
      if (contactsLoading || !hasMoreContacts || loadingMoreRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      // Check if we're near the bottom (within 200px)
      if (scrollHeight - scrollTop - clientHeight < 200) {
        loadMoreContacts();
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [currentView, contactsLoading, hasMoreContacts, loadMoreContacts]);

  const openContactModal = (contact: Contact | null = null) => {
    setEditingContact(contact);
    // Default to Quick Add when creating a new contact; edit stays full form
    if (contact) handleViewChange('create-contact');
    else handleViewChange('quick-add-contact');
  };

  const closeContactModal = () => {
    setSelectedContact(null);
    setEditingContact(null);
    setIsModalOpen(false);
    // Navigate back to previous view
    if (navigationStack.length > 1) {
      const newStack = [...navigationStack];
      newStack.pop();
      const previousView = newStack[newStack.length - 1];
      setNavigationStack(newStack);
      setCurrentView(previousView);
    } else {
      handleViewChange('dashboard');
    }
  };

  const openContactViewModal = (contact: Contact, collectionContext?: string, collectionName?: string, collectionId?: string) => {
    // If opening from a collection, capture current scroll position so we can restore it on back
    if (collectionId) {
      try {
        const container = document.getElementById('main-scroll-container');
        const pos = container ? (container.scrollTop || 0) : (window.scrollY || 0);
        sessionStorage.setItem(`gv:scroll:collection:${collectionId}`, String(pos));
      } catch { /* ignore */ }
    }
    // If we're already in contact-detail and switching to another contact, push current contact onto the local stack
    if (currentViewRef.current === 'contact-detail' && viewingContact?.id && viewingContact.id !== contact.id) {
      contactHistoryRef.current.push(viewingContact.id);
      // Push a browser history entry so the Back button triggers popstate and we can intercept
      try { window.history.pushState({ view: 'contact-detail', contactId: contact.id }, '', window.location.href); } catch { }
    }
    setViewingContact(contact);

    // Set collection context fields individually when present.
    // Rely primarily on collectionId to know we came from a collection.
    if (collectionId) {
      setViewContactCollectionId(collectionId);
    } else {
      setViewContactCollectionId(undefined);
    }
    if (typeof collectionName !== 'undefined') {
      setViewContactCollectionName(collectionName);
    } else {
      setViewContactCollectionName(undefined);
    }
    if (typeof collectionContext !== 'undefined') {
      setViewContactCollectionContext(collectionContext);
    } else {
      setViewContactCollectionContext(undefined);
    }

    // Navigate to contact detail page instead of opening modal
    handleViewChange('contact-detail');
  };

  const closeContactViewModal = () => {
    setViewingContact(null);
    setViewContactCollectionContext(undefined);
    setViewContactCollectionName(undefined);
    setViewContactCollectionId(undefined);
    setIsViewModalOpen(false);
  };

  const handleContactSave = () => {
    fetchContacts(); // Reload first page
    closeContactModal();
  };

  const handleContactViewSave = () => {
    // Just refresh contacts data, don't clear navigation state
    // The contact detail page handles its own navigation via onBack
    fetchContacts(0, false); // Reload first page to refresh data
  };

  const handleContactUpdate = () => {
    // Instead of reloading all contacts, just fetch the first page to refresh
    // This prevents disrupting search state and infinite scroll
    fetchContacts(0, false); // Reload only first page, don't append
  };

  // Treat Quick Add and Full Form as one logical page
  const isAddView = (view?: string | null) => view === 'quick-add-contact' || view === 'create-contact';

  // Replace top-of-stack when toggling between add views so back won't ping-pong
  const replaceTopView = (newView: string) => {
    setNavigationStack(prev => {
      const next = [...prev];
      if (next.length > 0) next[next.length - 1] = newView; else next.push(newView);
      return next;
    });
    setCurrentView(newView);
    // Mirror change in browser history without adding a new entry
    try {
      const state: any = { view: newView };
      if (newView === 'collection-view' && selectedCollectionId) state.collectionId = selectedCollectionId;
      if (newView === 'contact-detail') {
        if (viewContactCollectionId) state.collectionId = viewContactCollectionId;
        if (viewingContact?.id) state.contactId = viewingContact.id;
      }
      window.history.replaceState(state, '', window.location.href);
    } catch { /* ignore */ }
  };

  // Back handler that skips either add view entirely
  const handleBackSkippingAddViews = () => {
    if (navigationStack.length > 0) {
      let newStack = [...navigationStack];
      // Drop current
      newStack.pop();
      // Skip any trailing add views
      while (newStack.length > 0 && isAddView(newStack[newStack.length - 1])) newStack.pop();

      if (newStack.length > 0) {
        const prev = newStack[newStack.length - 1];
        setNavigationStack(newStack);
        setCurrentView(prev);
      } else {
        setNavigationStack(['dashboard']);
        setCurrentView('dashboard');
      }
    } else {
      setNavigationStack(['dashboard']);
      setCurrentView('dashboard');
    }
  };

  const handleViewChange = (newView: string) => {
    // Clear collection selection mode when navigating to contacts (fresh contacts view)
    if (newView === 'contacts' && collectionSelectionMode.enabled) {
      setCollectionSelectionMode({ enabled: false, collectionId: '', collectionName: '', existingMemberIds: [] });
    }

    // Add to navigation stack if it's a different view
    if (newView !== currentView) {
      // Reset tier filter when navigating away from contacts unless explicitly setting one
      if (currentView === 'contacts' && newView !== 'contacts') {
        setContactsTierFilter('all');
        // Clear collection selection mode when leaving contacts view
        setCollectionSelectionMode({ enabled: false, collectionId: '', collectionName: '', existingMemberIds: [] });
      }

      // Clear collection context when navigating away from collection views
      if (currentView === 'collection-view' && newView !== 'collection-view') {
        setSelectedCollectionId(null);
        // Clear any open modal's collection context
        if (isViewModalOpen) {
          setViewContactCollectionContext(undefined);
          setViewContactCollectionName(undefined);
          setViewContactCollectionId(undefined);
        }
      }

      setNavigationStack(prev => [...prev, newView]);
      setCurrentView(newView);

      // Push a new browser history entry for this in-app navigation (skip if handling back or very first render)
      if (!isHandlingPopstateRef.current && navigationStack.length > 0) {
        try {
          const state: any = { view: newView };
          if (newView === 'collection-view' && selectedCollectionId) {
            state.collectionId = selectedCollectionId;
          }
          if (newView === 'contact-detail') {
            if (viewContactCollectionId) state.collectionId = viewContactCollectionId;
            if (viewingContact?.id) state.contactId = viewingContact.id;
          }
          window.history.pushState(state, '', window.location.href);
        } catch { }
      }
    }
  };

  const handleSearchContacts = () => {
    setShouldFocusSearch(true);
    setContactsTierFilter('all'); // Reset tier filter when searching
    handleViewChange('contacts');
  };

  const handleViewContactsWithTierFilter = (tier: string) => {
    setContactsTierFilter(tier);
    setShouldFocusSearch(false);
    handleViewChange('contacts');
  };

  const handleShowCollectionView = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
    handleViewChange('collection-view');
  };

  const handleCreateCollection = () => {
    setEditingCollectionId(null);
    handleViewChange('collection-create');
  };

  const handleBackFromContactDetail = () => {
    // If we have intra-contact history, go back to the previous contact first
    if (contactHistoryRef.current.length > 0) {
      const prevId = contactHistoryRef.current.pop()!;
      (async () => {
        try {
          const row = await getContactCore(supabase as any, prevId);
          if (row) {
            setViewingContact(row as any as Contact);
            try { window.history.replaceState({ view: 'contact-detail', contactId: prevId }, '', window.location.href); } catch { }
          }
        } catch (err) {
          console.error('Failed to navigate back to previous contact', err);
        }
      })();
      return;
    }
    // Determine origin using collectionId only (message_context/name may be empty)
    const returnToCollectionId = viewContactCollectionId;
    const wasFromCollection = !!returnToCollectionId;

    // Clear contact detail state
    setViewingContact(null);
    setViewContactCollectionContext(undefined);
    setViewContactCollectionName(undefined);
    setViewContactCollectionId(undefined);

    // If the contact was opened from a collection, navigate back to that collection
    if (wasFromCollection && returnToCollectionId) {
      // Ensure collection ID is set before navigation
      setSelectedCollectionId(returnToCollectionId);

      // Navigate back to collection view on next tick to avoid a blank frame
      setTimeout(() => {
        setCurrentView('collection-view');
        // Clean up navigation stack to ensure proper back navigation
        setNavigationStack(prev => {
          const newStack = [...prev];
          // Remove 'contact-detail' from the end and ensure 'collection-view' is there
          if (newStack[newStack.length - 1] === 'contact-detail') {
            newStack.pop();
          }
          // Ensure collection-view is at the end
          if (newStack[newStack.length - 1] !== 'collection-view') {
            newStack.push('collection-view');
          }
          return newStack;
        });
      }, 0);
    } else {
      // Go back to previous view in navigation stack
      if (navigationStack.length > 1) {
        const newStack = [...navigationStack];
        newStack.pop(); // Remove current view
        const previousView = newStack[newStack.length - 1];
        setNavigationStack(newStack);
        setCurrentView(previousView);
        // If going back to contacts, emit a restore event for assistant search consumers
        if (previousView === 'contacts') {
          try {
            const q = sessionStorage.getItem('gv:assistantReturn') || sessionStorage.getItem('gv:lastAssistantSearch') || '';
            window.dispatchEvent(new CustomEvent('gv:assistant:restoreSearch', { detail: { query: q } } as any));
          } catch { /* ignore */ }
        }
      } else {
        handleViewChange('dashboard');
      }
    }
  };

  const handleBackFromCollection = () => {
    setSelectedCollectionId(null);
    // Clear any modal collection context when going back
    if (isViewModalOpen) {
      setViewContactCollectionContext(undefined);
      setViewContactCollectionName(undefined);
      setViewContactCollectionId(undefined);
    }
    handleViewChange('collections');
  };

  const handleNavigateToContactsForCollection = (collectionId: string, collectionName: string, existingMemberIds: string[]) => {
    setCollectionSelectionMode({
      enabled: true,
      collectionId,
      collectionName,
      existingMemberIds
    });
    handleViewChange('contacts');
  };

  const handleNavigateBackFromCollectionSelection = () => {
    // Keep the collection ID but disable selection mode
    const currentCollectionId = collectionSelectionMode.collectionId;
    setCollectionSelectionMode({ enabled: false, collectionId: '', collectionName: '', existingMemberIds: [] });

    // Ensure we stay on the same collection
    if (currentCollectionId) {
      // First, ensure the collection ID is set
      setSelectedCollectionId(currentCollectionId);

      // Then navigate back to collection-view
      // Use setTimeout to ensure state updates happen in the correct order
      setTimeout(() => {
        setCurrentView('collection-view');
        // Clean up navigation stack
        setNavigationStack(prev => {
          const newStack = [...prev];
          // Find the last occurrence of 'collection-view' or add it
          const collectionViewIndex = newStack.lastIndexOf('collection-view');
          if (collectionViewIndex === -1) {
            // If collection-view isn't in the stack, add it
            newStack.push('collection-view');
          } else {
            // Remove everything after the last collection-view
            newStack.length = collectionViewIndex + 1;
          }
          return newStack;
        });
      }, 0);
    } else {
      handleViewChange('collections');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <img
            src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
            alt="Vault Contact Logo"
            className="h-16 w-16 mx-auto mb-6 object-contain animate-pulse"
          />
          <SquareLoader size="sm" className="mb-4 mx-auto" />
          <p className="text-gray-400 font-light tracking-wide">ACCESSING VAULT...</p>
        </div>
      </div>
    );
  }

  // Check if this is the password reset route
  const currentPath = window.location.pathname;
  // Export help route: public access
  if (currentPath === '/export-help') {
    return <ContactExportHelp />;
  }

  // Public share routes: bypass auth and app chrome entirely
  if (currentPath.startsWith('/connect/v/') || currentPath.startsWith('/event/v/')) {
    return <ShareRouter />;
  }
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const type = hashParams.get('type');
  const recovery = hashParams.get('recovery');

  if (currentPath === '/auth/reset-password' || type === 'recovery' || recovery === 'true') {
    return <PasswordResetPage />;
  }

  if (!user) {
    return <LoginForm />;
  }

  // If an invite token is pending, show the accept/decline page first (before vault access checks)
  if (pendingInviteToken) {
    return <InviteAcceptPage token={pendingInviteToken} onDone={handleInviteDone} />;
  }

  // Gating removed: proceed directly after login

  // If vault access is restricted, show gate screens
  if (accessState !== 'ok') {
    return (
      <VaultGate
        state={accessState}
        slug={vaultSlug || ''}
        onCreate={async () => {
          if (!vaultSlug) return;
          await createVaultCore(supabase as any, vaultSlug, undefined);
          await reload();
        }}
        onRefresh={async () => reload()}
      />
    );
  }

  // If at root path (no slug), open preferred vault if set; else show home page
  if (!vaultSlug) {
    // Try to resolve and redirect to preferred vault once per render
    (async () => {
      try {
        const vId = await getPreferredVaultIdCore(supabase as any, user.id);
        if (vId) {
          const v = await getGroupByIdCore(supabase as any, vId);
          const slug = (v as any)?.slug as string | undefined;
          if (slug) {
            try {
              window.history.pushState({}, '', `/${slug}`);
              // Reload group context by reloading the page location minimally
              window.dispatchEvent(new PopStateEvent('popstate'));
            } catch {
              window.location.href = `/${slug}`;
            }
          }
        }
      } catch {
        // ignore
      }
    })();
    return <HomePage user={user} />;
  }

  // Show a tiny hold until the Startup Page has been applied to avoid flashing the wrong view
  if (!startupApplied) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <img
            src="https://res.cloudinary.com/digjsdron/image/upload/v1753107750/GriswoldVault_bjzwjw.webp"
            alt="Vault Contact Logo"
            className="h-16 w-16 mx-auto mb-6 object-contain animate-pulse"
          />
          <SquareLoader size="sm" className="mb-4 mx-auto" />
          <p className="text-gray-400 font-light tracking-wide">Loading your start view…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {notification && (
        <NotificationToast notification={notification} onClose={hideNotification} />
      )}
      <Sidebar
        currentView={currentView}
        onViewChange={handleViewChange}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isCollapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onSearchContacts={handleSearchContacts}
      />

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} h-screen overflow-hidden`}>
        {/* Mobile header with menu button */}
        <div className="lg:hidden bg-black border-b border-gray-800 px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700 hover:border-gray-600 transition-all duration-200"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button
              onClick={() => handleViewChange('dashboard')}
              title="Home"
              className="hover:opacity-80 transition-opacity"
            >
              <img src={vaultLogoUrl} alt="Vault Contact Logo" className="h-5 w-5 object-contain" />
            </button>
            {vaultName ? (
              <button
                onClick={() => handleViewChange('dashboard')}
                title="Home"
                className="text-left text-sm text-gray-300 font-medium truncate max-w-[40vw] hover:underline"
              >
                {vaultName}
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            {/* Green dot positioned on the right */}
            <div className="w-2 h-2 bg-green-400 animate-pulse" style={{ borderRadius: '9999px' }}></div>
          </div>
        </div>

        {/* Desktop header (group menu removed per request) */}
        <header className="hidden lg:block bg-black border-b border-gray-800 px-6 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="relative">
              <h1 className="text-2xl font-light text-white tracking-wide">
                {currentView === 'dashboard' && 'Dashboard'}
                {currentView === 'contacts' && 'People'}
                {currentView === 'collections' && 'Contact Collections'}
                {currentView === 'collection-view' && 'Collection Details'}
                {currentView === 'contact-detail' && 'Contact Details'}
                {currentView === 'create-contact' && 'Add New Contact'}
                {currentView === 'quick-add-contact' && 'Quick Add Contact'}
                {currentView === 'create-group' && 'Create New Group'}
                {currentView === 'map' && 'Contact Map'}
                {currentView === 'todo' && 'Follow up'}
                {currentView === 'calendar' && 'Calendar'}
                {currentView === 'settings' && 'Settings'}
                {currentView === 'ghl-management' && 'GoHighLevel Management'}
                {currentView === 'note-migration' && 'Note Migration'}
                {currentView === 'assistant' && 'Assistant'}
              </h1>
              {currentView === 'assistant' && (
                <div className="text-sm text-gray-400 mt-1">Ask, search, scan, or text — all in one place</div>
              )}
              {vaultName ? (
                <div className="text-sm text-gray-400 mt-1">{vaultName}</div>
              ) : null}
            </div>

            <div className="flex items-center gap-4">
              {/* Green dot positioned on the right */}
              <div className="w-2 h-2 bg-green-400 animate-pulse" style={{ borderRadius: '9999px' }}></div>
            </div>
          </div>
        </header>

        {/* Content area - this is what needs to scroll */}
        <div className="flex-1 overflow-y-auto" id="main-scroll-container">
          {/* Inline create-group removed */}
          <div className={`${currentView === 'dashboard' ? 'block' : 'hidden'}`}>
            <Dashboard
              contacts={contacts}
              user={user}
              onViewContacts={() => {
                setContactsTierFilter('all');
                handleViewChange('contacts');
              }}
              onViewContactsWithTierFilter={handleViewContactsWithTierFilter}
              onSearchContacts={handleSearchContacts}
              onAddContact={() => openContactModal()}
              onContactSelect={openContactViewModal}
            />
          </div>

          <div className={`${currentView === 'contacts' ? 'block' : 'hidden'}`}>
            <ContactList
              contacts={contacts}
              onContactSelect={openContactViewModal}
              onAddContact={() => openContactModal()}
              shouldFocusSearch={shouldFocusSearch}
              onSearchFocused={() => setShouldFocusSearch(false)}
              initialSearchTerm={pendingContactsSearchTerm}
              onLoadMore={loadMoreContacts}
              onLoadAll={fetchAllContacts}
              onResetToBasicView={resetToBasicView}
              onContactUpdate={handleContactUpdate}
              hasMoreContacts={hasMoreContacts}
              contactsLoading={contactsLoading}
              initialTierFilter={contactsTierFilter}
              user={user}
              collectionSelectionMode={collectionSelectionMode}
              onNavigateBack={handleNavigateBackFromCollectionSelection}
              hideSearchHeader={false}
              onServerSearch={async (query: string, page: number) => {
                const batchSize = 50;
                const from = page * batchSize;
                try {
                  const rows = activeGroupId
                    ? await listContactsForGroupCore(supabase as any, activeGroupId, { query, includeArchived: false as const, limit: batchSize, offset: from })
                    : await listContactsCore(supabase as any, { query, includeArchived: false as const, limit: batchSize, offset: from });
                  return (rows as any[]) || [];
                } catch (err) {
                  console.error('Server search failed', err);
                  throw err;
                }
              }}
            />
            {/* Loading indicator at the bottom */}
            {currentView === 'contacts' && contactsLoading && hasMoreContacts && (
              <div className="flex justify-center py-4">
                <SquareLoader size="sm" />
              </div>
            )}
          </div>

          <div className={`${currentView === 'collections' ? 'block' : 'hidden'}`}>
            <ContactCollections
              user={user}
              onContactSelect={openContactViewModal}
              onCollectionSelect={handleShowCollectionView}
              onCreateNew={handleCreateCollection}
            />
          </div>
          {/* Full-page create/edit collection */}
          {currentView === 'collection-create' && (
            <div>
              <CollectionEditorPage
                user={user}
                collectionId={editingCollectionId}
                onBack={() => handleViewChange('collections')}
                onSaved={() => {
                  // Let collections screen refresh and navigate back
                  window.dispatchEvent(new CustomEvent('gv:refreshCollections'));
                  handleViewChange('collections');
                }}
              />
            </div>
          )}

          {currentView === 'collection-view' && selectedCollectionId && (
            <div>
              <CollectionDetailsPage
                collectionId={selectedCollectionId}
                user={user}
                onBack={handleBackFromCollection}
                onContactSelect={openContactViewModal}
                onCollectionUpdate={() => { }} // Will refresh the collections list when going back
                onNavigateToContacts={handleNavigateToContactsForCollection}
              />
            </div>
          )}

          {currentView === 'contact-detail' && viewingContact ? (
            <div>
              <ContactDetailPage
                contact={viewingContact}
                user={user}
                personalProfile={personalProfile}
                collectionContext={viewContactCollectionContext}
                collectionName={viewContactCollectionName}
                collectionId={viewContactCollectionId}
                onBack={handleBackFromContactDetail}
                onSave={handleContactViewSave}
              />
            </div>
          ) : currentView === 'contact-detail' ? (
            // Safety fallback: if we're in contact-detail view but have no contact, go back
            <div className="min-h-screen bg-black flex items-center justify-center">
              <div className="text-center">
                <SquareLoader size="sm" className="mx-auto mb-4" />
                <p className="text-gray-400">Navigating back...</p>
              </div>
            </div>
          ) : null}

          <div className={`${currentView === 'create-contact' ? 'block' : 'hidden'}`}>
            <CreateContactPage
              user={user}
              editingContact={editingContact}
              onBack={handleBackSkippingAddViews}
              onSave={handleContactSave}
              onToggleQuickAdd={() => replaceTopView('quick-add-contact')}
            />
          </div>

          <div className={`${currentView === 'quick-add-contact' ? 'block' : 'hidden'}`}>
            <QuickAddContact
              user={user}
              onBack={handleBackSkippingAddViews}
              onSaved={() => {
                fetchContacts();
              }}
              onToggleFullForm={() => replaceTopView('create-contact')}
            />
          </div>

          {/* Global Floating Action Button */}
          <div
            className={`fixed bottom-6 right-6 transition-opacity ${sidebarOpen ? 'opacity-30 pointer-events-none z-30' : 'opacity-100 z-50'}`}
            aria-hidden={sidebarOpen ? true : undefined}
          >
            {(currentView === 'quick-add-contact' || currentView === 'create-contact' || currentView === 'assistant') ? null : (
              // Everywhere else: Plus FAB to start Quick Add
              <button
                onClick={() => openContactModal(null)}
                aria-label="Add contact"
                className="w-14 h-14 rounded-sm flex items-center justify-center bg-white text-black shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white/50 border border-gray-300"
                title="Add contact"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 5c.552 0 1 .448 1 1v5h5c.552 0 1 .448 1 1s-.448 1-1 1h-5v5c0 .552-.448 1-1 1s-1-.448-1-1v-5H6c-.552 0-1-.448-1-1s.448-1 1-1h5V6c0-.552.448-1 1-1z" />
                </svg>
              </button>
            )}
          </div>

          <div className={`${currentView === 'map' ? 'h-full' : 'hidden'}`}>
            <GoogleContactMap
              user={user}
              onContactSelect={openContactViewModal}
              isVisible={currentView === 'map'}
            />
          </div>

          <div className={`${currentView === 'todo' ? 'block' : 'hidden'}`}>
            <TodoPanel user={user} />
            {/* Mobile-only sticky back to calendar when navigated from it */}
            <div className="lg:hidden">
              {(() => {
                try {
                  const ret = sessionStorage.getItem('gv:returnTo');
                  if (ret === 'calendar') {
                    return (
                      <div className="fixed bottom-5 right-5 z-30">
                        <button
                          onClick={() => {
                            try { sessionStorage.removeItem('gv:returnTo'); } catch { }
                            handleViewChange('calendar');
                          }}
                          className="px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-gray-200 shadow"
                        >
                          Back to Calendar
                        </button>
                      </div>
                    );
                  }
                } catch { /* ignore */ }
                return null;
              })()}
            </div>
          </div>

          <div className={`${currentView === 'calendar' ? 'block' : 'hidden'}`}>
            <NewCalendarView user={user} />
          </div>



          {/* Assistant view */}
          <div className={`${currentView === 'assistant' ? 'h-full' : 'hidden'}`}>
            <ChatAssist user={user} />
          </div>

          {/* Full-page Create Group */}
          <div className={`${currentView === 'create-group' ? 'block' : 'hidden'}`}>
            <CreateGroupPage
              user={user}
              onBack={() => {
                // Navigate back to previous view or dashboard
                if (navigationStack.length > 1) {
                  const newStack = [...navigationStack];
                  newStack.pop();
                  const prev = newStack[newStack.length - 1];
                  setNavigationStack(newStack);
                  setCurrentView(prev);
                } else {
                  handleViewChange('dashboard');
                }
              }}
              onCreated={() => {
                // Go to People (contacts) after creation
                window.dispatchEvent(new CustomEvent('gv:navigateToPeople'));
              }}
            />
          </div>

          <div className={`${currentView === 'settings' ? 'block' : 'hidden'}`}>
            <Settings
              user={user}
              personalProfile={personalProfile}
              onPersonalProfileChange={setPersonalProfile}
              onContactSelect={openContactViewModal}
            />
          </div>

          <div className={`${currentView === 'ghl-management' ? 'block' : 'hidden'}`}>
            <GHLManagement />
          </div>

          <div className={`${currentView === 'note-migration' ? 'block' : 'hidden'}`}>
            <NoteMigrationDashboard />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <GroupProvider>
      <AppInner />
    </GroupProvider>
  );
}